import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { Button } from '../ui/Button'
import type { DeviceLifecycle, LifecycleEventType } from '@shared/types'

interface Props {
  siteId: string
  deviceId: string
  deviceLabel: string
  onStatusChange?: () => void
}

const EVENT_LABELS: Record<string, string> = {
  planned: 'Planned',
  ordered: 'Ordered',
  received: 'Received',
  installed: 'Installed',
  configured: 'Configured',
  active: 'Active',
  maintenance: 'Maintenance',
  offline: 'Offline',
  decommissioned: 'Decommissioned',
  rma: 'RMA',
}

const EVENT_COLORS: Record<string, string> = {
  planned: 'var(--text-2)',
  ordered: 'var(--accent)',
  received: 'var(--accent)',
  installed: 'var(--info)',
  configured: 'var(--info)',
  active: 'var(--ok)',
  maintenance: 'var(--accent)',
  offline: 'var(--err)',
  decommissioned: 'var(--color-infra)',
  rma: 'var(--err)',
}

const STATUS_OPTIONS: LifecycleEventType[] = [
  'planned', 'ordered', 'received', 'installed', 'configured', 'active',
  'maintenance', 'offline', 'decommissioned', 'rma',
]

export function DeviceLifecycleTimeline({ siteId, deviceId, deviceLabel: _deviceLabel, onStatusChange }: Props) {
  const [events, setEvents] = useState<DeviceLifecycle[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [newEventType, setNewEventType] = useState<LifecycleEventType>('active')
  const [newNote, setNewNote] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    api.get<DeviceLifecycle[]>(`/sites/${siteId}/devices/${deviceId}/lifecycle`)
      .then(setEvents)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId, deviceId])

  async function handleAddEvent() {
    setSaving(true)
    try {
      const event = await api.post<DeviceLifecycle>(`/sites/${siteId}/devices/${deviceId}/lifecycle`, {
        event_type: newEventType,
        notes: newNote || undefined,
      })
      setEvents(prev => [event, ...prev])
      setShowForm(false)
      setNewNote('')
      onStatusChange?.()
    } catch { /* ignore */ } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-lg)' }} />
  }

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Lifecycle
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'Update Status'}
        </Button>
      </div>

      {/* Status change form */}
      {showForm && (
        <div style={{
          padding: 12, background: 'var(--bg-inset)', borderRadius: 'var(--radius-lg)',
          marginBottom: 16,
        }}>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>
              Change status to:
            </label>
            <select
              value={newEventType}
              onChange={(e) => setNewEventType(e.target.value as LifecycleEventType)}
              style={{
                width: '100%', padding: '6px 8px', fontSize: 12, borderRadius: 6,
                border: '1px solid var(--border)', background: 'var(--bg-surface)',
              }}
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{EVENT_LABELS[opt]}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>
              Note:
            </label>
            <input
              type="text"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Optional note..."
              style={{
                width: '100%', padding: '6px 8px', fontSize: 12, borderRadius: 6,
                border: '1px solid var(--border)', background: 'var(--bg-surface)',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <Button
            size="sm"
            onClick={handleAddEvent}
            disabled={saving}
            loading={saving}
          >
            {saving ? 'Saving...' : 'Update Status'}
          </Button>
        </div>
      )}

      {/* Timeline */}
      {events.length > 0 ? (
        <div style={{ position: 'relative', paddingLeft: 20 }}>
          {/* Vertical line */}
          <div style={{
            position: 'absolute', left: 5, top: 6, bottom: 6,
            width: 2, background: 'var(--border-light)',
          }} />

          {events.map((event, i) => {
            const color = EVENT_COLORS[event.event_type] || 'var(--color-infra)'
            const isFirst = i === 0
            return (
              <div key={event.id} style={{ position: 'relative', paddingBottom: 16 }}>
                {/* Dot */}
                <div style={{
                  position: 'absolute', left: -17, top: 2,
                  width: 10, height: 10, borderRadius: '50%',
                  background: isFirst ? color : 'var(--bg-surface)',
                  border: `2px solid ${color}`,
                  zIndex: 1,
                }} />

                <div className="flex justify-between items-start">
                  <div>
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      color: isFirst ? color : 'var(--text)',
                    }}>
                      {EVENT_LABELS[event.event_type] || event.event_type}
                    </span>
                    {event.notes && (
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                        {event.notes}
                      </div>
                    )}
                    {event.performer_name && (
                      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 1 }}>
                        By: {event.performer_name}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', marginLeft: 8 }}>
                    {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>No lifecycle events recorded</p>
      )}
    </div>
  )
}
